import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AvatarModule } from '@fc-shared/ui/avatar/avatar.module';
import { QuickSearchCompanyItem } from '@fc-search/models/quick-search';
import { QuickSearchFacadeService } from '@fc-search/services/quick-search-facade.service';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { TooltipDirective } from '@fc-shared/ui/tooltips/tooltip.directive';

@Component({
  selector: 'fc-qs-company-result',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, AvatarModule, ButtonComponent, TooltipDirective],
  template: `
    <div
      class="flex gap-12 items-center max-w-full relative py-8 pl-14 pr-16 hover:bg-secondary-50 cursor-pointer group border-l-2"
      [class.border-white]="!selected()"
      [class.border-primary-600]="selected()"
      [class.bg-secondary-50]="selected()"
      [routerLink]="['/crm/main/companies', result().obj.id]"
    >
      <div class="shrink-0">
        <fc-entity-avatar
          type="company"
          [entity]="companyForAvatar()"
          [size]="40"
          [fontSize]="20"
        />
      </div>

      <div class="flex flex-col gap-4 grow min-w-0">
        <span class="text-sm font-medium text-grey-900 truncate">
          {{ result().obj.name }}
        </span>
        <div
          class="flex flex-wrap items-center gap-4 text-xs font-medium text-secondary-500 truncate"
        >
          @if (result().obj.emails.length > 0) {
            <span>{{ result().obj.emails[0] }}</span>
          }
          @if (result().obj.phoneNumbers.length > 0) {
            <div
              class="size-6 rounded-full bg-secondary-300 first:hidden"
            ></div>
            <span>{{ result().obj.phoneNumbers[0] }}</span>
          }
          @if (result().searchData?.contactCount; as count) {
            <div
              class="size-6 rounded-full bg-secondary-300 first:hidden"
            ></div>
            <span>{{ count }} contacts</span>
          }
        </div>
      </div>
      <div
        class="gap-4 p-4 rounded-xl bg-white border-secondary-100 shadow-sm"
        [class.opacity-0]="!selected()"
        [class.opacity-100]="selected()"
        [class.group-hover:opacity-100]="!selected()"
      >
        <div
          fcTooltip="Open in new tab"
          [showTooltip]="selected() && activeButtonIndex() === 0"
        >
          <fc-button
            variant="filled"
            color="primary"
            [focused]="activeButtonIndex() === 0"
            icon="icon-new-window"
            size="sm"
            iconPosition="only"
            (click)="viewDetails(); $event.stopPropagation()"
          />
        </div>
      </div>
    </div>
  `,
})
export class QsCompanyResultComponent {
  result = input.required<QuickSearchCompanyItem>();
  selected = input(false);
  activeButtonIndex = input(-1);
  initial = computed(() => (this.result().obj.name?.[0] ?? '').toUpperCase());
  quickAction = output();
  private router = inject(Router);
  private facade = inject(QuickSearchFacadeService);

  companyForAvatar = computed(() => ({
    id: this.result().obj.id,
    name: this.result().obj.name,
    color: this.result().obj.color,
    photo: this.result().obj.photo,
  }));

  viewDetails(): void {
    this.quickAction.emit();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/crm/main/companies/' + this.result().obj.id,
      ]),
    );
    window.open(url, '_blank');
    this.facade.clearAndClose();
  }

  activateButton(index: number): void {
    if (index === 0) {
      this.viewDetails();
    }
  }
}
